import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { ShieldCheck, Copy, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AdminSecurity() {
  const { user } = useAuth();

  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showRecoveryRegeneration, setShowRecoveryRegeneration] = useState(false);
  const [recoveryVerificationCode, setRecoveryVerificationCode] = useState("");
  const [regeneratingRecoveryCodes, setRegeneratingRecoveryCodes] = useState(false);
  const [showReplacementVerification, setShowReplacementVerification] = useState(false);
  const [replacementVerificationCode, setReplacementVerificationCode] = useState("");

  const startSetup = async (currentCode = null) => {
    setBusy(true);

    try {
      const { data } = await api.post(
        "/auth/mfa/setup/start",
        currentCode ? { code: currentCode.trim() } : {}
      );

      setSetup(data);
      setCode("");
      setRecoveryCodes([]);
      setShowReplacementVerification(false);
      setReplacementVerificationCode("");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Unable to start MFA setup."
      );
    } finally {
      setBusy(false);
    }
  };

  const verifyCurrentMfaForReplacement = async (e) => {
    e.preventDefault();

    if (!replacementVerificationCode.trim()) {
      toast.error("Enter your current authenticator code.");
      return;
    }

    await startSetup(replacementVerificationCode);
  };
  const verifySetup = async (e) => {
    e.preventDefault();
    setBusy(true);

    try {
      const { data } = await api.post("/auth/mfa/setup/verify", { code });

      setRecoveryCodes(data.recovery_codes || []);
      setSetup(null);
      setCode("");

      toast.success("Multi-factor authentication is now enabled.");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Unable to verify authentication code."
      );
    } finally {
      setBusy(false);
    }
  };

  const regenerateRecoveryCodes = async (e) => {
    e.preventDefault();

    if (!recoveryVerificationCode.trim()) {
      toast.error("Enter your current authenticator code.");
      return;
    }

    setRegeneratingRecoveryCodes(true);

    try {
      const { data } = await api.post(
        "/auth/mfa/recovery-codes/regenerate",
        { code: recoveryVerificationCode.trim() }
      );

      setRecoveryCodes(data.recovery_codes || []);
      setRecoveryVerificationCode("");
      setShowRecoveryRegeneration(false);

      toast.success(
        "New recovery codes generated. Previous recovery codes are now invalid."
      );
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Unable to regenerate recovery codes."
      );
    } finally {
      setRegeneratingRecoveryCodes(false);
    }
  };

  const copyText = async (value, message) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(message);
    } catch {
      toast.error("Unable to copy automatically.");
    }
  };

  const copyRecoveryCodes = () => {
    copyText(
      recoveryCodes.join("\n"),
      "Recovery codes copied."
    );
  };

  return (
    <div className="max-w-3xl" data-testid="page-admin-security">
      <div className="flex items-center gap-3">
        <div className="rounded-sm bg-navy p-2.5 text-cyan">
          <ShieldCheck size={22} />
        </div>

        <div>
          <h1 className="font-heading text-2xl font-bold text-navy">
            Security
          </h1>
          <p className="mt-1 text-sm text-slate">
            Manage multi-factor authentication for your Admin account.
          </p>
        </div>
      </div>

      <section className="mt-8 rounded-md border border-brandborder bg-white p-6">
        <div className="flex items-start justify-between gap-5">
          <div>
            <h2 className="font-heading text-lg font-bold text-navy">
              Authenticator MFA
            </h2>

            <p className="mt-2 text-sm leading-relaxed text-slate">
              Protect {user?.email || "your Admin account"} with a time-based
              one-time password from an authenticator app.
            </p>
          </div>

          <span
            className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${
              user?.mfa_enabled
                ? "bg-emerald-50 text-emerald-700"
                : "bg-amber-50 text-amber-700"
            }`}
          >
            {user?.mfa_enabled ? "Enabled" : "Not enabled"}
          </span>
        </div>

        {!setup && recoveryCodes.length === 0 && (
          <button
            type="button"
            onClick={() => {
              if (user?.mfa_enabled) {
                setShowReplacementVerification(true);
                setReplacementVerificationCode("");
              } else {
                startSetup();
              }
            }}
            disabled={busy}
            className="mt-6 bg-navy text-white font-semibold text-sm px-5 py-3 rounded-sm hover:bg-brandblue transition-colors disabled:opacity-50"
          >
            {busy
              ? "Starting…"
              : user?.mfa_enabled
                ? "Replace authenticator MFA"
                : "Set up authenticator MFA"}
          </button>
        )}

        {user?.mfa_enabled &&
          showReplacementVerification &&
          !setup &&
          recoveryCodes.length === 0 && (
            <form
              onSubmit={verifyCurrentMfaForReplacement}
              className="mt-7 border-t border-brandborder pt-6"
            >
              <h3 className="font-semibold text-navy">
                Confirm your current authenticator
              </h3>

              <p className="mt-2 text-sm leading-relaxed text-slate">
                Enter a code from your existing authenticator before a new
                authenticator setup key is created. Your current MFA remains
                active until the replacement is successfully verified.
              </p>

              <div className="mt-5">
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate mb-2">
                  Current authenticator code
                </label>

                <input
                  value={replacementVerificationCode}
                  onChange={(e) =>
                    setReplacementVerificationCode(e.target.value)
                  }
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  className="w-full max-w-xs rounded-sm border border-brandborder px-4 py-3 text-sm outline-none focus:border-brandblue"
                  placeholder="123456"
                  required
                />
              </div>

              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  type="submit"
                  disabled={busy}
                  className="bg-brandblue text-white font-semibold text-sm px-5 py-3 rounded-sm hover:bg-navy transition-colors disabled:opacity-50"
                >
                  {busy ? "Verifying..." : "Verify and continue"}
                </button>

                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setShowReplacementVerification(false);
                    setReplacementVerificationCode("");
                  }}
                  className="border border-brandborder bg-white text-navy font-semibold text-sm px-5 py-3 rounded-sm hover:border-brandblue transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

        {setup && (
          <form onSubmit={verifySetup} className="mt-7 border-t border-brandborder pt-6">
            <h3 className="font-semibold text-navy">
              Add TWORAY to your authenticator
            </h3>

            <p className="mt-2 text-sm leading-relaxed text-slate">
              In Microsoft Authenticator, Google Authenticator, 1Password or
              another TOTP app, choose to add an account manually and enter
              the key below.
            </p>

            <div className="mt-5 rounded-sm bg-lightbg border border-brandborder p-5">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate">
                Scan QR code
              </div>

              <div className="mt-4 inline-flex rounded-md bg-white p-4 border border-brandborder">
                <QRCodeSVG
                  value={setup.provisioning_uri}
                  size={220}
                  level="M"
                  includeMargin={false}
                />
              </div>

              <p className="mt-4 text-sm text-slate">
                Open your authenticator app, choose to add an account, then scan this QR code.
              </p>
            </div>

            <div className="mt-4 rounded-sm bg-lightbg border border-brandborder p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate">
                Manual setup key
              </div>

              <div className="mt-2 flex items-center gap-3">
                <code className="break-all text-sm font-semibold text-navy">
                  {setup.manual_key}
                </code>

                <button
                  type="button"
                  onClick={() =>
                    copyText(setup.manual_key, "Setup key copied.")
                  }
                  className="ml-auto shrink-0 text-brandblue hover:text-navy"
                  aria-label="Copy setup key"
                >
                  <Copy size={18} />
                </button>
              </div>
            </div>

            <div className="mt-5">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate mb-2">
                6-digit authenticator code
              </label>

              <input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
                className="w-full max-w-xs rounded-sm border border-brandborder px-4 py-3 text-sm outline-none focus:border-brandblue"
                placeholder="123456"
                required
              />
            </div>

            <button
              disabled={busy}
              className="mt-5 bg-brandblue text-white font-semibold text-sm px-5 py-3 rounded-sm hover:bg-navy transition-colors disabled:opacity-50"
            >
              {busy ? "Verifying…" : "Verify and enable MFA"}
            </button>
          </form>
        )}

        {user?.mfa_enabled && recoveryCodes.length === 0 && (
          <div className="mt-7 border-t border-brandborder pt-6">
            <div className="flex items-center gap-2 text-navy">
              <KeyRound size={19} className="text-brandblue" />
              <h3 className="font-semibold">Recovery codes</h3>
            </div>

            <p className="mt-2 text-sm leading-relaxed text-slate">
              Generate a new set if your existing recovery codes have been
              lost, exposed or used. Your previous recovery codes will stop
              working immediately.
            </p>

            {!showRecoveryRegeneration ? (
              <button
                type="button"
                onClick={() => {
                  setShowRecoveryRegeneration(true);
                  setRecoveryVerificationCode("");
                }}
                className="mt-5 border border-brandborder bg-white text-navy font-semibold text-sm px-5 py-3 rounded-sm hover:border-brandblue transition-colors"
              >
                Generate new recovery codes
              </button>
            ) : (
              <form
                onSubmit={regenerateRecoveryCodes}
                className="mt-5 rounded-sm border border-brandborder bg-lightbg p-5"
              >
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate mb-2">
                  Confirm with your current authenticator code
                </label>

                <input
                  value={recoveryVerificationCode}
                  onChange={(e) =>
                    setRecoveryVerificationCode(e.target.value)
                  }
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  className="w-full max-w-xs rounded-sm border border-brandborder bg-white px-4 py-3 text-sm outline-none focus:border-brandblue"
                  placeholder="123456"
                  required
                />

                <div className="mt-4 flex flex-wrap gap-3">
                  <button
                    type="submit"
                    disabled={regeneratingRecoveryCodes}
                    className="bg-brandblue text-white font-semibold text-sm px-5 py-3 rounded-sm hover:bg-navy transition-colors disabled:opacity-50"
                  >
                    {regeneratingRecoveryCodes
                      ? "Verifying..."
                      : "Verify and regenerate"}
                  </button>

                  <button
                    type="button"
                    disabled={regeneratingRecoveryCodes}
                    onClick={() => {
                      setShowRecoveryRegeneration(false);
                      setRecoveryVerificationCode("");
                    }}
                    className="border border-brandborder bg-white text-navy font-semibold text-sm px-5 py-3 rounded-sm hover:border-brandblue transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {recoveryCodes.length > 0 && (
          <div className="mt-7 border-t border-brandborder pt-6">
            <div className="flex items-center gap-2 text-navy">
              <KeyRound size={19} className="text-brandblue" />
              <h3 className="font-semibold">Save your recovery codes</h3>
            </div>

            <p className="mt-2 text-sm leading-relaxed text-slate">
              These codes are shown only now. Each code can be used once if
              your authenticator is unavailable. Store them securely in your
              password manager and do not share them.
            </p>

            <div className="mt-5 grid gap-2 sm:grid-cols-2">
              {recoveryCodes.map((recoveryCode) => (
                <code
                  key={recoveryCode}
                  className="rounded-sm border border-brandborder bg-lightbg px-3 py-2 text-sm font-semibold text-navy"
                >
                  {recoveryCode}
                </code>
              ))}
            </div>

            <button
              type="button"
              onClick={copyRecoveryCodes}
              className="mt-5 inline-flex items-center gap-2 bg-navy text-white font-semibold text-sm px-5 py-3 rounded-sm hover:bg-brandblue transition-colors"
            >
              <Copy size={16} />
              Copy all recovery codes
            </button>

            <p className="mt-4 text-xs font-semibold text-amber-700">
              Do not leave this page until you have saved these recovery codes.
            </p>
          </div>
        )}
      </section>

    </div>
  );
}
